import useUiTranslation from "../utils/useUiTranslation";
import ProcurementIdentitySettings from '../components/itemMaster/ProcurementIdentitySettings';
import MaintenanceApprovalReportingSettings from '../components/MaintenanceApprovalReportingSettings';
import TechnicalReviewSettings from '../components/TechnicalReviewSettings';
import SurgicalConsignmentSettings from '../components/SurgicalConsignmentSettings';
import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { useAuth } from '../hooks/useAuth';
import { useAccessControl } from '../hooks/useAccessControl';
import { hasPermission, hasAnyPermission } from '../utils/permissions';
import { filterUsersBySearch } from '../utils/filterUsers';
import useWarehouses from '../hooks/useWarehouses';
import DocumentBrandingSettings from '../components/DocumentBrandingSettings';
import { defaultContractApprovalRules } from '../config/contractApprovalRules';
import {
  Building2, ChevronRight, FileSliders, FolderKanban, KeyRound, Network,
  Printer, Route, Search, ShieldCheck, UserCog, Users, Warehouse,
} from 'lucide-react';

const initialUserEditState = {
  role: '',
  department_id: '',
  section_id: '',
  section_ids: [],
  can_request_medication: false,
  warehouse_id: '',
};

const initialNewDepartment = { name: '', type: 'operational' };
const initialNewSection = { department_id: '', name: '' };
const initialNewRoute = {
  request_type: '',
  department_type: 'medical',
  approval_level: '1',
  role: '',
  min_amount: '',
  max_amount: '',
  warehouse_id: '',
};

const defaultAutoAssignmentTypes = [
  'Maintenance',
  'Non-stock',
  'Stock',
  'IT',
  'Logbook',
  'Medical Device',
  'Medication',
];

const initialAutoAssignmentRule = {
  request_type: 'Maintenance',
  warehouse_id: '',
  assignee_user_id: '',
  is_active: true,
};

const managementTabs = {
  users: ['Users', 'Accounts and team assignments', Users, 'People'],
  accountRequests: ['Account requests', 'Review pending access requests', UserCog, 'People'],
  roles: ['Roles', 'Maintain organizational roles', ShieldCheck, 'People'],
  departments: ['Departments', 'Structure departments and sections', Building2, 'Organization'],
  warehouses: ['Warehouses', 'Manage inventory locations', Warehouse, 'Organization'],
  projects: ['Projects', 'Control project visibility', FolderKanban, 'Organization'],
  routes: ['Approval routes', 'Configure request approval paths', Route, 'Workflow'],
  autoAssignments: ['Auto assignments', 'Route work to the right owners', Network, 'Workflow'],
  printServices: ['Print services', 'Department printing preferences', Printer, 'Workflow'],
  contractApprovalRules: ['Contract rules', 'Contract approval stages and gates', FileSliders, 'Workflow'],
  itemIdentity: ['Item Master policy', 'Control the transition from free-text procurement', ShieldCheck, 'Workflow'],
  technicalReview: ['Technical reviews', 'Technical responsibilities and receiving inspection requirements', ShieldCheck, 'Workflow'],
  surgicalConsignment: ['Surgical consignments', 'Eligible departments and automatic clinical routing', ShieldCheck, 'Workflow'],
  approvalReporting: ['Approval reporting', 'Saved maintenance overdue target', ShieldCheck, 'Workflow'],
  permissions: ['Permissions', 'Fine-grained user capabilities', KeyRound, 'Access control'],
  interfaceAccess: ['Interface access', 'Map features to permissions', ShieldCheck, 'Access control'],
};

const DepartmentVisibilityChecklist = ({
  departments,
  selectedDepartmentIds,
  onChange,
  disabled = false,
  ariaLabel,
  className = '',
}) => {
  const uiText = useUiTranslation();
  const selectedIds = Array.isArray(selectedDepartmentIds) ? selectedDepartmentIds : [];
  const selectedSet = new Set(selectedIds.map((id) => Number(id)));
  const departmentIds = departments.map((department) => Number(department.id)).filter(Boolean);

  const toggleDepartment = (departmentId) => {
    const normalizedId = Number(departmentId);
    const nextIds = selectedSet.has(normalizedId)
      ? selectedIds.filter((id) => Number(id) !== normalizedId)
      : [...selectedIds, normalizedId];

    onChange(nextIds);
  };

  return (
    <div
      className={`mt-1 rounded border border-gray-200 bg-white ${className}`.trim()}
      role="group"
      aria-label={ariaLabel}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={selectedIds.length === 0}
            onChange={() => onChange([])}
            disabled={disabled}
          />{uiText("All departments")}</label>
        <button
          type="button"
          className="text-xs font-semibold text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
          onClick={() => onChange(departmentIds)}
          disabled={disabled || departmentIds.length === 0}
        >
          Check all listed
        </button>
      </div>
      <div className="max-h-44 space-y-2 overflow-y-auto px-3 py-2">
        {departments.length === 0 ? (
          <p className="text-xs text-gray-500">No departments available.</p>
        ) : (
          departments.map((department) => {
            const departmentId = Number(department.id);
            return (
              <label
                key={department.id}
                className="flex items-center gap-2 rounded px-1 py-1 text-sm text-gray-700 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  checked={selectedSet.has(departmentId)}
                  onChange={() => toggleDepartment(departmentId)}
                  disabled={disabled}
                />
                <span>{department.name}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

const Management = () => {
  const uiText = useUiTranslation();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState('');
  const [roleSuccess, setRoleSuccess] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [editingRole, setEditingRole] = useState({ id: null, name: '' });
  const [deletingRoleId, setDeletingRoleId] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [routesVersionLabel, setRoutesVersionLabel] = useState('');
  const [autoAssignmentRules, setAutoAssignmentRules] = useState([]);
  const [autoAssignmentAssignees, setAutoAssignmentAssignees] = useState([]);
  const [autoAssignmentWarehouses, setAutoAssignmentWarehouses] = useState([]);
  const [autoAssignmentTypes, setAutoAssignmentTypes] = useState(defaultAutoAssignmentTypes);
  const [autoAssignmentForm, setAutoAssignmentForm] = useState(initialAutoAssignmentRule);
  const [editingAutoAssignmentId, setEditingAutoAssignmentId] = useState(null);
  const [editingAutoAssignment, setEditingAutoAssignment] = useState(initialAutoAssignmentRule);
  const [autoAssignmentMessage, setAutoAssignmentMessage] = useState('');
  const [accountRequests, setAccountRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [warehouseForm, setWarehouseForm] = useState({ name: '' });
  const [warehouseMessage, setWarehouseMessage] = useState('');
  const [warehouseError, setWarehouseError] = useState('');
  const [printSettings, setPrintSettings] = useState(null);
  const [printSettingsDepartmentId, setPrintSettingsDepartmentId] = useState('');
  const [printSettingsMessage, setPrintSettingsMessage] = useState('');
  const [printSettingsError, setPrintSettingsError] = useState('');
  const [loadingPrintSettings, setLoadingPrintSettings] = useState(false);
  const [savingPrintSettings, setSavingPrintSettings] = useState(false);
  const [savingWarehouse, setSavingWarehouse] = useState(false);
  const [editingWarehouseId, setEditingWarehouseId] = useState(null);
  const [editingWarehouseName, setEditingWarehouseName] = useState('');
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [organizationMessage, setOrganizationMessage] = useState('');
  const [managementSearch, setManagementSearch] = useState('');

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [projectMessage, setProjectMessage] = useState('');
  const [projectError, setProjectError] = useState('');

  const [usersError, setUsersError] = useState('');
  const [departmentsError, setDepartmentsError] = useState('');
  const [routesError, setRoutesError] = useState('');
  const [autoAssignmentError, setAutoAssignmentError] = useState('');
  const [loadingAutoAssignments, setLoadingAutoAssignments] = useState(false);
  const [simulationInput, setSimulationInput] = useState('{\n  "changes": [],\n  "scenarios": []\n}');
  const [simulationResult, setSimulationResult] = useState(null);
  const [requestsError, setRequestsError] = useState('');

  const [tab, setTab] = useState('users');
  const [editUserId, setEditUserId] = useState(null);
  const [editData, setEditData] = useState(initialUserEditState);

  const [userFilters, setUserFilters] = useState({
    search: '',
    role: 'all',
    showInactive: false,
  });

  const [requestFilters, setRequestFilters] = useState({
    search: '',
    status: 'all',
  });

  const [newDept, setNewDept] = useState(initialNewDepartment);
  const [newSection, setNewSection] = useState(initialNewSection);
  const [editRoutes, setEditRoutes] = useState({});
  const [newRoute, setNewRoute] = useState(initialNewRoute);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDepartmentIds, setNewProjectDepartmentIds] = useState([]);
  const [savingProjectVisibilityId, setSavingProjectVisibilityId] = useState(null);

  const { user } = useAuth();
  const {
    warehouses,
    warehouseMap,
    loading: warehousesLoading,
    error: warehousesError,
    refresh: refreshWarehouses,
  } = useWarehouses();
  const {
    resources: uiResources,
    loading: uiAccessLoading,
    error: uiAccessLoadError,
    refresh: refreshUiAccess,
  } = useAccessControl();

  const permissionFlags = useMemo(() => {
    const currentUser = user || {};
    return {
      canManageUsers: hasPermission(currentUser, 'users.manage'),
      canViewUsers: hasAnyPermission(currentUser, ['users.view', 'users.manage']),
      canManageAccountRequests: hasPermission(currentUser, 'users.manage'),
      canManageDepartments: hasPermission(currentUser, 'departments.manage'),
      canManageRoutes: hasPermission(currentUser, 'permissions.manage'),
      canManageProjects: hasPermission(currentUser, 'projects.manage'),
      canManageRoles: hasAnyPermission(currentUser, ['roles.manage', 'permissions.manage']),
      canManagePermissions: hasPermission(currentUser, 'permissions.manage'),
      canManageContractApprovalRules: hasAnyPermission(currentUser, ['contracts.manage', 'permissions.manage']),
      canManageAutoAssignments: hasPermission(currentUser, 'requests.manage'),
      canManagePrintServiceSettings: hasPermission(currentUser, 'departments.manage') ||
        ['Admin', 'SCM'].includes(currentUser.role),
    };
  }, [user]);

  const {
    canViewUsers,
    canManageAccountRequests,
    canManageDepartments,
    canManageRoutes,
    canManageProjects,
    canManageRoles,
    canManagePermissions,
    canManageContractApprovalRules,
    canManageAutoAssignments,
    canManagePrintServiceSettings,
  } = permissionFlags;

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (canViewUsers) tabs.push('users');
    if (canManageAccountRequests) tabs.push('accountRequests');
    if (canManageDepartments) tabs.push('departments');
    if (canManageDepartments) tabs.push('warehouses');
    if ((user?.permissions || []).includes('technical-inspections.manage') && ['scm','admin','procurementsupervisor','procurement supervisor'].includes(String(user?.role || '').toLowerCase())) tabs.push('technicalReview');
    if (canManageDepartments || canManagePermissions) tabs.push('surgicalConsignment');
    if (canManageRoutes) tabs.push('routes');
    if (canManageAutoAssignments) tabs.push('autoAssignments');
    if (canManagePrintServiceSettings) tabs.push('printServices');
    if (canManageProjects) tabs.push('projects');
    if (canManageRoles) {
      tabs.push('roles');
    }
    if (canManagePermissions) {
      tabs.push('itemIdentity');
      tabs.push('approvalReporting');
      tabs.push('permissions');
      tabs.push('interfaceAccess');
    }
    if (canManageContractApprovalRules) tabs.push('contractApprovalRules');
    return tabs;
  }, [
    canViewUsers,
    canManageAccountRequests,
    canManageDepartments,
    canManageRoutes,
    canManageProjects,
    canManageRoles,
    canManageAutoAssignments,
    canManagePrintServiceSettings,
    canManagePermissions,
    canManageContractApprovalRules,
    user,
  ]);

  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState('');
  const [permissionsSuccess, setPermissionsSuccess] = useState('');
  const [selectedPermissionUserId, setSelectedPermissionUserId] = useState('');
  const [permissionUserSearch, setPermissionUserSearch] = useState('');
  const [inlinePermissionsEditorOpen, setInlinePermissionsEditorOpen] = useState(false);
  const [selectedUserPermissions, setSelectedUserPermissions] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [editingAccessKey, setEditingAccessKey] = useState('');
  const [editingAccessPermissions, setEditingAccessPermissions] = useState([]);
  const [editingAccessRequireAll, setEditingAccessRequireAll] = useState(false);
  const [customAccessPermission, setCustomAccessPermission] = useState('');
  const [accessConfigError, setAccessConfigError] = useState('');
  const [accessConfigSuccess, setAccessConfigSuccess] = useState('');
  const [savingAccessConfig, setSavingAccessConfig] = useState(false);
  const [contractRulesDraft, setContractRulesDraft] = useState(() =>
    JSON.stringify(defaultContractApprovalRules, null, 2),
  );
  const [contractRulesError, setContractRulesError] = useState('');
  const [contractRulesSuccess, setContractRulesSuccess] = useState('');

  const warehouseRoles = useMemo(
    () => new Set(['warehousemanager', 'warehouse_manager', 'warehousekeeper', 'warehouse_keeper']),
    [],
  );

  const requiresWarehouseAssignment = (roleName) =>
    warehouseRoles.has(String(roleName || '').toLowerCase());

  /* eslint-disable react-hooks/exhaustive-deps -- Data loaders are declared below these effects and are gated by permission/tab state. */
  useEffect(() => {
    if (canViewUsers || canManageRoutes || canManagePermissions || canManageRoles) {
      fetchRoles();
    }
    if (canViewUsers || canManagePermissions || canManageRoles) {
      fetchUsers();
    }
    if (canManageDepartments || canViewUsers || canManagePermissions || canManageRoles || canManageProjects || canManagePrintServiceSettings) {
      fetchDepartments();
    }
    if (canManageRoutes) {
      fetchRoutes();
    }
    if (canManageAccountRequests) {
      fetchAccountRequests();
    }
    if (canManageProjects) {
      fetchProjects();
    }
  }, [
    canViewUsers,
    canManageRoutes,
    canManagePermissions,
    canManageRoles,
    canManageDepartments,
    canManageAccountRequests,
    canManageProjects,
    canManagePrintServiceSettings,
  ]);

  useEffect(() => {
    if (tab === 'users' && canViewUsers) {
      fetchUsers();
      fetchDepartments();
      if (!roles.length) {
        fetchRoles();
      }
      if (canManagePermissions && !availablePermissions.length) {
        fetchPermissionsCatalog();
      }
    }
    if (tab === 'departments' && canManageDepartments) {
      fetchDepartments();
    }
    if (tab === 'roles' && canManageRoles) {
      fetchRoles();
    }
    if (tab === 'warehouses' && canManageDepartments) {
      refreshWarehouses();
      fetchDepartments();
    }
    if (tab === 'routes' && canManageRoutes) {
      fetchRoutes();
      if (!roles.length) {
        fetchRoles();
      }
    }
    if (tab === 'permissions' && canManagePermissions) {
      fetchUsers();
      if (!availablePermissions.length) {
        fetchPermissionsCatalog();
      }
    }
    if (tab === 'accountRequests' && canManageAccountRequests) {
      fetchAccountRequests();
    }
    if (tab === 'projects' && canManageProjects) {
      fetchProjects();
      fetchDepartments();
    }
    if (tab === 'autoAssignments' && canManageAutoAssignments) {
      fetchAutoAssignmentRules();
    }
    if (tab === 'printServices' && canManagePrintServiceSettings) {
      fetchPrintServiceSettings();
      fetchDepartments();
    }
  }, [
    tab,
    canViewUsers,
    canManageDepartments,
    canManageRoutes,
    canManagePermissions,
    canManageAccountRequests,
    canManageProjects,
    canManageRoles,
    canManageAutoAssignments,
    canManagePrintServiceSettings,
    roles.length,
    availablePermissions.length,
  ]);

  useEffect(() => {
    if (availableTabs.length === 0) {
      return;
    }
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0]);
    }
  }, [availableTabs, tab]);

  useEffect(() => setManagementSearch(''), [tab]);

  useEffect(() => {
    if (canManagePermissions) {
      fetchPermissionsCatalog();
    } else {
      setAvailablePermissions([]);
      setSelectedPermissionUserId('');
      setSelectedUserPermissions([]);
      setPermissionsError('');
      setPermissionsSuccess('');
    }
  }, [canManagePermissions]);

  useEffect(() => {
    if (!canManagePermissions) {
      return;
    }
    if (!selectedPermissionUserId && users.length > 0) {
      setSelectedPermissionUserId(String(users[0].id));
    }
  }, [canManagePermissions, users, selectedPermissionUserId]);

  useEffect(() => {
    if (!canManagePermissions || !selectedPermissionUserId) {
      return;
    }
    fetchUserPermissions(selectedPermissionUserId);
  }, [canManagePermissions, selectedPermissionUserId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchUsers = async () => {
    if (!(canViewUsers || canManagePermissions)) return;
    setLoadingUsers(true);
    setUsersError('');
    try {
      const res = await api.get('/users');
      setUsers(res.data || []);
    } catch (err) {
      console.error('Failed to load users', err);
      const message = err?.response?.data?.message || 'Failed to load users. Please try again.';
      setUsersError(message);
      if (canManagePermissions && !canViewUsers) {
        setPermissionsError(message);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchDepartments = async () => {
    if (!(canManageDepartments || canViewUsers || canManagePermissions || canManageProjects || canManagePrintServiceSettings)) return;
    setLoadingDepartments(true);
    setDepartmentsError('');
    try {
      const res = await api.get('/departments');
      setDepartments(res.data || []);
    } catch (err) {
      console.error('Failed to load departments', err);
      setDepartmentsError('Failed to load departments.');
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchRoles = async () => {
    if (!(canViewUsers || canManageRoutes || canManagePermissions || canManageRoles)) return;
    setRolesLoading(true);
    setRolesError('');
    try {
      const res = await api.get('/roles');
      setRoles(res.data || []);
    } catch (err) {
      console.error('Failed to load roles', err);
      setRolesError('Failed to load roles.');
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchRoutes = async () => {
    if (!canManageRoutes) return;
    setLoadingRoutes(true);
    setRoutesError('');
    try {
      const res = await api.get('/approval-routes');
      const responseData = res.data || {};
      if (Array.isArray(responseData)) {
        setRoutes(responseData);
        setRoutesVersionLabel('legacy');
      } else {
        setRoutes(responseData.routes || []);
        setRoutesVersionLabel(responseData.active_version?.version_label || '');
      }
    } catch (err) {
      console.error('Failed to load approval routes', err);
      setRoutesError('Failed to load approval routes.');
    } finally {
      setLoadingRoutes(false);
    }
  };


  const fetchAutoAssignmentRules = async () => {
    if (!canManageAutoAssignments) return;
    setLoadingAutoAssignments(true);
    setAutoAssignmentError('');
    try {
      const res = await api.get('/request-auto-assignment-rules');
      setAutoAssignmentRules(res.data?.rules || []);
      setAutoAssignmentAssignees(res.data?.assignees || []);
      setAutoAssignmentWarehouses(res.data?.warehouses || []);
      setAutoAssignmentTypes(res.data?.request_types || defaultAutoAssignmentTypes);
    } catch (err) {
      console.error('Failed to load auto-assignment rules', err);
      setAutoAssignmentError(err?.response?.data?.message || 'Failed to load auto-assignment rules.');
    } finally {
      setLoadingAutoAssignments(false);
    }
  };

  const normalizeAutoAssignmentPayload = (rule) => ({
    request_type: rule.request_type,
    warehouse_id: rule.request_type === 'Stock' && rule.warehouse_id ? Number(rule.warehouse_id) : null,
    assignee_user_id: Number(rule.assignee_user_id),
    is_active: rule.is_active !== false,
  });

  const saveAutoAssignmentRule = async (rule, id = null) => {
    if (!canManageAutoAssignments) return;
    setAutoAssignmentError('');
    setAutoAssignmentMessage('');

    if (!rule.request_type || !rule.assignee_user_id) {
      setAutoAssignmentError('Choose a request type and assignee.');
      return;
    }

    if (rule.request_type === 'Stock' && !rule.warehouse_id) {
      setAutoAssignmentError('Stock rules must select the submitted warehouse.');
      return;
    }

    try {
      const payload = normalizeAutoAssignmentPayload(rule);
      if (id) {
        await api.put(`/request-auto-assignment-rules/${id}`, payload);
        setEditingAutoAssignmentId(null);
      } else {
        await api.post('/request-auto-assignment-rules', payload);
        setAutoAssignmentForm(initialAutoAssignmentRule);
      }
      setAutoAssignmentMessage('Auto-assignment rule saved.');
      fetchAutoAssignmentRules();
    } catch (err) {
      console.error('Failed to save auto-assignment rule', err);
      setAutoAssignmentError(err?.response?.data?.message || 'Unable to save auto-assignment rule.');
    }
  };

  const deleteAutoAssignmentRule = async (id) => {
    if (!canManageAutoAssignments || !window.confirm('Delete this auto-assignment rule?')) return;
    setAutoAssignmentError('');
    setAutoAssignmentMessage('');
    try {
      await api.delete(`/request-auto-assignment-rules/${id}`);
      setAutoAssignmentMessage('Auto-assignment rule deleted.');
      fetchAutoAssignmentRules();
    } catch (err) {
      console.error('Failed to delete auto-assignment rule', err);
      setAutoAssignmentError(err?.response?.data?.message || 'Unable to delete auto-assignment rule.');
    }
  };

  const runRouteSimulation = async () => {
    if (!canManageRoutes) return;

    setRoutesError('');
    setSimulationResult(null);

    try {
      const payload = JSON.parse(simulationInput || '{}');
      const res = await api.post('/approval-routes/simulate', payload);
      setSimulationResult(res.data);
    } catch (err) {
      const parseError = err instanceof SyntaxError;
      setRoutesError(
        parseError
          ? 'Simulation payload must be valid JSON.'
          : err?.response?.data?.message || 'Failed to run simulation.',
      );
    }
  };

  const fetchAccountRequests = async () => {
    if (!canManageAccountRequests) return;
    setLoadingRequests(true);
    setRequestsError('');
    try {
      const res = await api.get('/auth/register-requests');
      setAccountRequests(res.data?.requests || []);
    } catch (err) {
      console.error('Failed to load account requests', err);
      setRequestsError('Failed to load account requests.');
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchProjects = async () => {
    if (!canManageProjects) return;
    setLoadingProjects(true);
    setProjectError('');
    setProjectMessage('');
    try {
      const res = await api.get('/projects/management');
      setProjects(res.data || []);
    } catch (err) {
      console.error('Failed to load projects', err);
      setProjectError(err?.response?.data?.message || 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };


  const fetchPrintServiceSettings = async () => {
    if (!canManagePrintServiceSettings) return;
    setLoadingPrintSettings(true);
    setPrintSettingsError('');
    try {
      const res = await api.get('/print-service-requests/settings');
      const settings = res.data?.settings || null;
      setPrintSettings(settings);
      setPrintSettingsDepartmentId(settings?.department_id ? String(settings.department_id) : '');
    } catch (err) {
      console.error('Failed to load print service settings', err);
      setPrintSettingsError(err?.response?.data?.message || 'Failed to load print service settings.');
    } finally {
      setLoadingPrintSettings(false);
    }
  };

  const savePrintServiceSettings = async () => {
    if (!canManagePrintServiceSettings) return;
    setSavingPrintSettings(true);
    setPrintSettingsError('');
    setPrintSettingsMessage('');
    try {
      const res = await api.put('/print-service-requests/settings', {
        department_id: printSettingsDepartmentId ? Number(printSettingsDepartmentId) : null,
      });
      const settings = res.data?.settings || null;
      setPrintSettings(settings);
      setPrintSettingsDepartmentId(settings?.department_id ? String(settings.department_id) : '');
      setPrintSettingsMessage(res.data?.message || 'IT Department Queue department link saved.');
    } catch (err) {
      console.error('Failed to save print service settings', err);
      setPrintSettingsError(err?.response?.data?.message || 'Failed to save print service settings.');
    } finally {
      setSavingPrintSettings(false);
    }
  };

  const getProjectDepartmentIds = (project) =>
    Array.isArray(project?.visible_departments)
      ? project.visible_departments.map((department) => Number(department.id)).filter(Boolean)
      : [];

  const formatProjectVisibility = (project) => {
    const visibleDepartments = Array.isArray(project?.visible_departments)
      ? project.visible_departments
      : [];

    if (visibleDepartments.length === 0) {
      return 'All departments';
    }

    return visibleDepartments.map((department) => department.name).join(', ');
  };

  const updateProjectVisibility = async (project, departmentIds) => {
    setProjectError('');
    setProjectMessage('');
    setSavingProjectVisibilityId(project.id);

    try {
      const res = await api.patch(`/projects/${project.id}/departments`, {
        department_ids: departmentIds,
      });
      const updatedProject = res.data?.project || project;
      setProjects((prev) =>
        prev.map((item) => (item.id === project.id ? updatedProject : item))
      );
      setProjectMessage(res.data?.message || `Project visibility updated`);
    } catch (err) {
      console.error('Failed to update project visibility', err);
      setProjectError(err?.response?.data?.message || 'Failed to update project visibility');
    } finally {
      setSavingProjectVisibilityId(null);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!canManageRoles) return;
    if (!newRoleName.trim()) {
      setRolesError('Role name is required.');
      return;
    }

    setSavingRole(true);
    setRolesError('');
    setRoleSuccess('');

    try {
      await api.post('/roles', { name: newRoleName.trim() });
      setNewRoleName('');
      setRoleSuccess('Role created successfully.');
      fetchRoles();
    } catch (err) {
      setRolesError(err?.response?.data?.message || 'Failed to create role.');
    } finally {
      setSavingRole(false);
    }
  };

  const startEditingRole = (role) => {
    setEditingRole({ id: role.id, name: role.name });
    setRolesError('');
    setRoleSuccess('');
  };

  const cancelEditingRole = () => {
    setEditingRole({ id: null, name: '' });
  };

  const handleUpdateRole = async () => {
    if (!canManageRoles || !editingRole.id) return;
    if (!editingRole.name.trim()) {
      setRolesError('Role name is required.');
      return;
    }

    setSavingRole(true);
    setRolesError('');
    setRoleSuccess('');

    try {
      await api.put(`/roles/${editingRole.id}`, { name: editingRole.name.trim() });
      setRoleSuccess('Role updated successfully.');
      setEditingRole({ id: null, name: '' });
      fetchRoles();
    } catch (err) {
      setRolesError(err?.response?.data?.message || 'Failed to update role.');
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!canManageRoles || !roleId) return;
    if (!window.confirm('Delete this role? This cannot be undone.')) {
      return;
    }

    setDeletingRoleId(roleId);
    setRolesError('');
    setRoleSuccess('');

    try {
      await api.delete(`/roles/${roleId}`);
      setRoleSuccess('Role deleted.');
      fetchRoles();
    } catch (err) {
      setRolesError(err?.response?.data?.message || 'Failed to delete role.');
    } finally {
      setDeletingRoleId(null);
    }
  };

  const fetchPermissionsCatalog = async () => {
    if (!canManagePermissions) return;
    setPermissionsLoading(true);
    setPermissionsError('');
    try {
      const res = await api.get('/permissions');
      setAvailablePermissions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load permissions', err);
      setPermissionsError('Failed to load permissions. Please try again.');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const fetchUserPermissions = async (userId) => {
    if (!canManagePermissions || !userId) return;
    setPermissionsLoading(true);
    setPermissionsError('');
    setPermissionsSuccess('');
    try {
      const res = await api.get(`/permissions/users/${userId}`);
      setSelectedUserPermissions(res.data?.permissions || []);
    } catch (err) {
      console.error('Failed to load user permissions', err);
      setPermissionsError('Failed to load user permissions.');
      setSelectedUserPermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const togglePermissionForUser = (code) => {
    setPermissionsSuccess('');
    setSelectedUserPermissions((prev) => {
      if (prev.includes(code)) {
        return prev.filter((permission) => permission !== code);
      }
      return [...prev, code];
    });
  };

  const saveUserPermissions = async () => {
    if (!canManagePermissions || !selectedPermissionUserId) {
      return;
    }
    setSavingPermissions(true);
    setPermissionsError('');
    setPermissionsSuccess('');
    try {
      await api.put(`/permissions/users/${selectedPermissionUserId}`, {
        permissions: selectedUserPermissions,
      });
      setPermissionsSuccess('Permissions updated successfully.');
    } catch (err) {
      console.error('Failed to update user permissions', err);
      setPermissionsError(err?.response?.data?.message || 'Failed to update user permissions.');
    } finally {
      setSavingPermissions(false);
    }
  };

  const openUserPermissionsEditor = (targetUser) => {
    if (!canManagePermissions || !targetUser?.id) return;
    setInlinePermissionsEditorOpen(true);
    setSelectedPermissionUserId(String(targetUser.id));
    fetchUserPermissions(targetUser.id);
  };

  const selectedPermissionUser = useMemo(
    () => users.find((entry) => String(entry.id) === String(selectedPermissionUserId)) || null,
    [selectedPermissionUserId, users],
  );

  const matchingPermissionUsers = useMemo(
    () => filterUsersBySearch(users, permissionUserSearch),
    [permissionUserSearch, users],
  );

  const permissionUserOptions = useMemo(() => {
    if (!selectedPermissionUser || matchingPermissionUsers.includes(selectedPermissionUser)) {
      return matchingPermissionUsers;
    }
    return [selectedPermissionUser, ...matchingPermissionUsers];
  }, [matchingPermissionUsers, selectedPermissionUser]);

  const renderInlineUserPermissionsEditor = () => {
    if (!canManagePermissions || !inlinePermissionsEditorOpen || !selectedPermissionUserId) return null;

    const urgentPermission = availablePermissions.find(
      (permission) => permission.code === 'requests.mark-urgent-on-submit',
    );
    const remainingPermissions = availablePermissions.filter(
      (permission) => permission.code !== 'requests.mark-urgent-on-submit',
    );

    const renderPermissionCheckbox = (permission, extraClassName = '') => {
      const checked = selectedUserPermissions.includes(permission.code);
      return (
        <label
          key={permission.code}
          className={`flex cursor-pointer items-start gap-3 rounded border border-gray-200 bg-gray-50 p-3 hover:border-blue-300 ${extraClassName}`}
        >
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={checked}
            onChange={() => togglePermissionForUser(permission.code)}
            disabled={permissionsLoading || savingPermissions}
          />
          <div>
            <div className="text-sm font-semibold text-gray-800">{permission.name}</div>
            <p className="text-xs text-gray-600">{permission.description}</p>
            <p className="text-xs text-gray-400">Code: {permission.code}</p>
          </div>
        </label>
      );
    };

    return (
      <div className="mb-4 rounded border border-blue-200 bg-blue-50 p-4 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-blue-900">
              User permissions
            </h3>
            <p className="text-sm text-blue-800">
              Editing permissions for {selectedPermissionUser?.name || 'selected user'}.
            </p>
          </div>
          <button
            type="button"
            className="text-sm font-semibold text-blue-700 hover:underline"
            onClick={() => setInlinePermissionsEditorOpen(false)}
          >
            Close
          </button>
        </div>

        {permissionsSuccess && (
          <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">
            {permissionsSuccess}
          </div>
        )}
        {permissionsError && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">
            {permissionsError}
          </div>
        )}

        {permissionsLoading ? (
          <p className="mt-3 text-sm text-gray-600">Loading permissions...</p>
        ) : availablePermissions.length === 0 ? (
          <p className="mt-3 text-sm text-gray-600">No permissions available.</p>
        ) : (
          <>
            {urgentPermission && (
              <div className="mt-3">
                {renderPermissionCheckbox(
                  urgentPermission,
                  'border-amber-300 bg-amber-50 hover:border-amber-400',
                )}
              </div>
            )}
            <details className="mt-3">
              <summary className="cursor-pointer text-sm font-semibold text-blue-800">
                Show all permissions
              </summary>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {remainingPermissions.map((permission) => renderPermissionCheckbox(permission))}
              </div>
            </details>
          </>
        )}

        <div className="mt-4 flex justify-end">
          <button
            onClick={saveUserPermissions}
            disabled={savingPermissions || permissionsLoading || !selectedPermissionUserId}
            className={`rounded px-4 py-2 text-white ${
              savingPermissions || permissionsLoading || !selectedPermissionUserId
                ? 'bg-gray-400'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {savingPermissions ? uiText("Saving...") : 'Save permissions'}
          </button>
        </div>
      </div>
    );
  };

  const startEditingAccessResource = (resource) => {
    if (!resource) {
      return;
    }

    setAccessConfigError('');
    setAccessConfigSuccess('');
    setEditingAccessKey(resource.key);
    setEditingAccessPermissions(
      Array.isArray(resource.permissions)
        ? resource.permissions
            .map((code) => (typeof code === 'string' ? code.toLowerCase() : ''))
            .filter(Boolean)
        : []
    );
    setEditingAccessRequireAll(Boolean(resource.requireAll));
    setCustomAccessPermission('');
  };

  const cancelEditingAccessResource = () => {
    setEditingAccessKey('');
    setEditingAccessPermissions([]);
    setEditingAccessRequireAll(false);
    setCustomAccessPermission('');
  };

  const toggleAccessPermissionForResource = (code) => {
    if (typeof code !== 'string') {
      return;
    }

    const normalized = code.trim().toLowerCase();
    if (!normalized) {
      return;
    }

    setEditingAccessPermissions((prev) => {
      const exists = prev.some((entry) => entry.toLowerCase() === normalized);
      if (exists) {
        return prev.filter((entry) => entry.toLowerCase() !== normalized);
      }
      return [...prev, normalized];
    });
  };

  const addCustomPermissionToResource = () => {
    const normalized = customAccessPermission.trim().toLowerCase();
    if (!normalized) {
      return;
    }

    setEditingAccessPermissions((prev) => {
      if (prev.some((entry) => entry.toLowerCase() === normalized)) {
        return prev;
      }
      return [...prev, normalized];
    });

    setCustomAccessPermission('');
  };

  const saveInterfaceAccess = async () => {
    if (!canManagePermissions || !editingAccessKey) {
      return;
    }

    setSavingAccessConfig(true);
    setAccessConfigError('');
    setAccessConfigSuccess('');

    try {
      await api.put(`/ui-access/${editingAccessKey}`, {
        permissions: editingAccessPermissions,
        requireAll: editingAccessRequireAll,
      });
      setAccessConfigSuccess('Interface access updated successfully.');
      cancelEditingAccessResource();
      await refreshUiAccess();
    } catch (err) {
      console.error('Failed to update interface access', err);
      setAccessConfigError(err?.response?.data?.message || 'Failed to update interface access.');
    } finally {
      setSavingAccessConfig(false);
    }
  };

  const deactivateUser = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await api.patch(`/users/${id}/deactivate`);
      fetchUsers();
    } catch (err) {
      console.error('Failed to deactivate user', err);
      alert('Unable to deactivate this user.');
    }
  };

  const startEdit = (user) => {
    setEditUserId(user.id);
    setEditData({
      role: user.role || '',
      department_id: user.department_id ? String(user.department_id) : '',
      section_id: user.section_id ? String(user.section_id) : '',
      section_ids: Array.isArray(user.assigned_section_ids)
        ? user.assigned_section_ids.map((sectionId) => String(sectionId))
        : user.section_id
          ? [String(user.section_id)]
          : [],
      can_request_medication: Boolean(user.can_request_medication),
      warehouse_id: user.warehouse_id ? String(user.warehouse_id) : '',
    });
  };

  const cancelEdit = () => {
    setEditUserId(null);
    setEditData(initialUserEditState);
  };

  const saveEdit = async (id) => {
    const needsWarehouse = requiresWarehouseAssignment(editData.role);
    if (needsWarehouse && !editData.warehouse_id) {
      alert('Select the warehouse this user manages before saving.');
      return;
    }

    try {
      await api.patch(`/users/${id}/assign`, {
        role: editData.role,
        department_id: editData.department_id ? Number(editData.department_id) : null,
        section_id: editData.section_ids[0] ? Number(editData.section_ids[0]) : null,
        section_ids: editData.section_ids.map((sectionId) => Number(sectionId)),
        warehouse_id: editData.warehouse_id ? Number(editData.warehouse_id) : null,
        can_request_medication: editData.can_request_medication,
      });
      setEditUserId(null);
      setEditData(initialUserEditState);
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user', err);
      alert('Unable to update user details.');
    }
  };

  const approveAccountRequest = async (id) => {
    if (!window.confirm('Approve this account request?')) return;
    try {
      await api.post(`/auth/register-requests/${id}/approve`);
      fetchAccountRequests();
      fetchUsers();
    } catch (err) {
      console.error('Failed to approve account request', err);
      alert(err.response?.data?.message || 'Failed to approve request.');
    }
  };

  const rejectAccountRequest = async (id) => {
    if (!window.confirm('Reject this account request?')) return;
    const reason = window.prompt('Reason for rejection (optional):', '') || '';
    try {
      await api.post(`/auth/register-requests/${id}/reject`, {
        reason: reason.trim() || undefined,
      });
      fetchAccountRequests();
    } catch (err) {
      console.error('Failed to reject account request', err);
      alert(err.response?.data?.message || 'Failed to reject request.');
    }
  };

  const addProject = async (event) => {
    event.preventDefault();
    const trimmed = newProjectName.trim();
    if (!trimmed) {
      setProjectError('Project name is required');
      return;
    }

    setProjectError('');
    setProjectMessage('');

    try {
      await api.post('/projects', {
        name: trimmed,
        department_ids: newProjectDepartmentIds,
      });
      setNewProjectName('');
      setNewProjectDepartmentIds([]);
      setProjectMessage(`Project "${trimmed}" added successfully`);
      fetchProjects();
    } catch (err) {
      console.error('Failed to add project', err);
      setProjectError(err?.response?.data?.message || 'Failed to add project');
    }
  };

  const deactivateProject = async (project) => {
    if (!window.confirm(`Deactivate the project "${project.name}"?`)) return;

    setProjectError('');
    setProjectMessage('');

    try {
      const res = await api.patch(`/projects/${project.id}/deactivate`);
      const updatedProject = res.data?.project || { ...project, is_active: false };
      setProjects((prev) =>
        prev.map((item) => (item.id === project.id ? updatedProject : item))
      );
      setProjectMessage(res.data?.message || `Project "${project.name}" deactivated`);
    } catch (err) {
      console.error('Failed to deactivate project', err);
      setProjectError(err?.response?.data?.message || 'Failed to deactivate project');
    }
  };

  const addDepartment = async () => {
    if (!newDept.name.trim()) {
      alert('Department name is required.');
      return;
    }
    if (!window.confirm('Add this department?')) return;
    try {
      await api.post('/departments', newDept);
      setNewDept(initialNewDepartment);
      fetchDepartments();
    } catch (err) {
      console.error('Failed to add department', err);
      alert('Unable to add department.');
    }
  };

  const addSection = async () => {
    if (!newSection.department_id) {
      alert('Please choose a department.');
      return;
    }
    if (!newSection.name.trim()) {
      alert('Section name is required.');
      return;
    }
    if (!window.confirm('Add this section?')) return;
    try {
      await api.post(`/departments/${newSection.department_id}/sections`, {
        name: newSection.name,
      });
      setNewSection(initialNewSection);
      fetchDepartments();
    } catch (err) {
      console.error('Failed to add section', err);
      alert('Unable to add section.');
    }
  };

  const saveDepartment = async () => {
    const name = editingDepartment?.name?.trim();
    if (!name) return setDepartmentsError('Department name is required.');
    setDepartmentsError('');
    try {
      await api.put(`/departments/${editingDepartment.id}`, { name, type: editingDepartment.type });
      setEditingDepartment(null);
      setOrganizationMessage('Department updated successfully.');
      fetchDepartments();
    } catch (err) {
      setDepartmentsError(err?.response?.data?.message || 'Unable to update department.');
    }
  };

  const saveSection = async () => {
    const name = editingSection?.name?.trim();
    if (!name) return setDepartmentsError('Section name is required.');
    setDepartmentsError('');
    try {
      await api.put(`/departments/${editingSection.departmentId}/sections/${editingSection.id}`, { name });
      setEditingSection(null);
      setOrganizationMessage('Section updated successfully.');
      fetchDepartments();
    } catch (err) {
      setDepartmentsError(err?.response?.data?.message || 'Unable to update section.');
    }
  };

  const startEditingWarehouse = (warehouse) => {
    setEditingWarehouseId(warehouse.id);
    setEditingWarehouseName(warehouse.name || '');
    setWarehouseMessage('');
    setWarehouseError('');
  };

  const cancelWarehouseEdit = () => {
    setEditingWarehouseId(null);
    setEditingWarehouseName('');
  };

  const addWarehouse = async () => {
    const trimmed = warehouseForm.name.trim();
    if (!trimmed) {
      setWarehouseError('Warehouse name is required.');
      return;
    }
    if (!window.confirm('Add this warehouse?')) return;

    setSavingWarehouse(true);
    setWarehouseError('');
    setWarehouseMessage('');

    try {
      await api.post('/warehouses', { name: trimmed });
      setWarehouseForm({ name: '' });
      setWarehouseMessage('Warehouse added successfully.');
      await Promise.all([refreshWarehouses(), fetchDepartments()]);
    } catch (err) {
      console.error('Failed to add warehouse', err);
      setWarehouseError(err?.response?.data?.message || 'Unable to add warehouse.');
    } finally {
      setSavingWarehouse(false);
    }
  };

  const saveWarehouseEdit = async () => {
    if (!editingWarehouseId) return;
    const trimmed = editingWarehouseName.trim();
    if (!trimmed) {
      setWarehouseError('Warehouse name cannot be empty.');
      return;
    }

    setSavingWarehouse(true);
    setWarehouseError('');
    setWarehouseMessage('');

    try {
      await api.put(`/warehouses/${editingWarehouseId}`, {
        name: trimmed,
      });
      setWarehouseMessage('Warehouse updated successfully.');
      cancelWarehouseEdit();
      await Promise.all([refreshWarehouses(), fetchDepartments()]);
    } catch (err) {
      console.error('Failed to update warehouse', err);
      setWarehouseError(err?.response?.data?.message || 'Unable to update warehouse.');
    } finally {
      setSavingWarehouse(false);
    }
  };

  const normalizeRoutePayload = (route) => ({
    ...route,
    approval_level: route.approval_level === '' ? null : Number(route.approval_level),
    min_amount:
      route.min_amount === '' || route.min_amount === null
        ? null
        : Number(route.min_amount),
    max_amount:
      route.max_amount === '' || route.max_amount === null
        ? null
        : Number(route.max_amount),
    warehouse_id:
      route.warehouse_id === '' || route.warehouse_id === null || route.warehouse_id === undefined
        ? null
        : Number(route.warehouse_id),
  });

  const saveRoute = async (route) => {
    const payload = normalizeRoutePayload(route);
    try {
      if (route.id) {
        await api.put(`/approval-routes/${route.id}`, payload);
      } else {
        await api.post('/approval-routes', payload);
        setNewRoute(initialNewRoute);
      }
      setEditRoutes((prev) => {
        if (!route.id) return prev;
        const copy = { ...prev };
        delete copy[route.id];
        return copy;
      });
      fetchRoutes();
    } catch (err) {
      console.error('Failed to save approval route', err);
      alert('Unable to save approval route.');
    }
  };

  const deleteRoute = async (id) => {
    if (!window.confirm('Delete this route?')) return;
    try {
      await api.delete(`/approval-routes/${id}`);
      fetchRoutes();
    } catch (err) {
      console.error('Failed to delete route', err);
      alert('Unable to delete approval route.');
    }
  };

  const departmentMap = useMemo(() => {
    const map = new Map();
    departments.forEach((dept) => {
      map.set(dept.id, dept);
      dept.sections?.forEach((section) => {
        map.set(`section-${section.id}`, section);
      });
    });
    return map;
  }, [departments]);

  const sectionOptions = useMemo(() => {
    const map = new Map();
    departments.forEach((dept) => {
      dept.sections?.forEach((section) => {
        map.set(section.id, section.name);
      });
    });
    return map;
  }, [departments]);

  const filteredUsers = useMemo(() => {
    const search = userFilters.search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesSearch =
        !search ||
        user.name?.toLowerCase().includes(search) ||
        user.email?.toLowerCase().includes(search);
      const matchesRole =
        userFilters.role === 'all' ||
        (user.role && user.role.toLowerCase() === userFilters.role.toLowerCase());
      const matchesActive = userFilters.showInactive || user.is_active;
      return matchesSearch && matchesRole && matchesActive;
    });
  }, [users, userFilters]);

  const filteredRequests = useMemo(() => {
    const search = requestFilters.search.trim().toLowerCase();
    return accountRequests
      .filter((req) => {
        const matchesSearch =
          !search ||
          req.name?.toLowerCase().includes(search) ||
          req.email?.toLowerCase().includes(search);
        const matchesStatus =
          requestFilters.status === 'all' || req.status === requestFilters.status;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      });
  }, [accountRequests, requestFilters]);

  const matchesManagementSearch = (...values) => {
    const query = managementSearch.trim().toLowerCase();
    return !query || values.some((value) => String(value || '').toLowerCase().includes(query));
  };
  const filteredDepartments = departments.filter((department) => matchesManagementSearch(
    department.name, department.type, ...(department.sections || []).map((section) => section.name),
  ));
  const filteredWarehouses = warehouses.filter((warehouse) =>
    matchesManagementSearch(warehouse.name, warehouse.type));
  const filteredRoles = roles.filter((role) => matchesManagementSearch(role.name));
  const filteredProjects = projects.filter((project) => matchesManagementSearch(
    project.name, project.is_active === false ? 'inactive' : 'active', formatProjectVisibility(project),
  ));
  const filteredRoutes = routes.filter((route) => matchesManagementSearch(
    route.request_type, route.department_type, route.role, warehouseMap[route.warehouse_id]?.name,
  ));
  const filteredAutoAssignmentRules = autoAssignmentRules.filter((rule) => matchesManagementSearch(
    rule.request_type, rule.assignee_name, rule.assignee_email, warehouseMap[rule.warehouse_id]?.name,
  ));

  const activeUserCount = useMemo(
    () => users.filter((user) => user.is_active).length,
    [users]
  );
  const activeProjectCount = useMemo(
    () => projects.filter((project) => project.is_active !== false).length,
    [projects]
  );
  const navigationGroups = useMemo(
    () => ['People', 'Organization', 'Workflow', 'Access control']
      .map((group) => ({
        group,
        items: availableTabs.filter((id) => managementTabs[id]?.[3] === group),
      }))
      .filter(({ items }) => items.length),
    [availableTabs],
  );
  const currentTab = managementTabs[tab] || managementTabs.users;
  const renderUsers = () => {
    if (!canViewUsers) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to view the user directory.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <div className="flex flex-col gap-3 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">{uiText("Search")}</label>
            <input
              type="search"
              className="mt-1 w-full rounded border border-gray-300 p-2"
              placeholder="Search by name or email"
              value={userFilters.search}
              onChange={(e) =>
                setUserFilters((prev) => ({ ...prev, search: e.target.value }))
              }
            />
          </div>
          <div className="lg:w-48">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">{uiText("Role")}</label>
            <select
              className="mt-1 w-full rounded border border-gray-300 p-2"
              value={userFilters.role}
              onChange={(e) =>
                setUserFilters((prev) => ({ ...prev, role: e.target.value }))
              }
            >
              <option value="all">All roles</option>
              {roles.map((role) => (
                <option key={role.id} value={role.name}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={userFilters.showInactive}
              onChange={(e) =>
                setUserFilters((prev) => ({
                  ...prev,
                  showInactive: e.target.checked,
                }))
              }
            />
            Show inactive users
          </label>
        </div>
        {usersError && <p className="text-sm text-red-600">{usersError}</p>}
      </div>
      {renderInlineUserPermissionsEditor()}
      {loadingUsers ? (
        <p>{uiText("Loading users...")}</p>
      ) : filteredUsers.length === 0 ? (
        <p className="text-sm text-gray-600">No users match the current filters.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-200 text-left">
              <th className="p-2">{uiText("Name")}</th>
              <th className="p-2">{uiText("Employee ID")}</th>
              <th className="p-2">{uiText("Email")}</th>
              <th className="p-2">{uiText("Role")}</th>
              <th className="p-2">{uiText("Department")}</th>
              <th className="p-2">{uiText("Section")}</th>
              <th className="p-2">{uiText("Warehouse")}</th>
              <th className="p-2">Medication?</th>
              <th className="p-2">{uiText("Active")}</th>
              <th className="p-2">{uiText("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => {
              const department = user.department_id
                ? departmentMap.get(user.department_id)
                : null;
              const assignedSectionNames = Array.isArray(user.assigned_section_ids)
                ? user.assigned_section_ids.map((sectionId) => sectionOptions.get(sectionId)).filter(Boolean)
                : [];
              const section = assignedSectionNames.length > 0
                ? assignedSectionNames.join(', ')
                : user.section_id
                  ? sectionOptions.get(user.section_id)
                  : null;
              const warehouse = user.warehouse_id
                ? warehouseMap.get(user.warehouse_id)
                : null;
              const warehouseName = warehouse?.name || user.warehouse_name;
              return (
                <tr key={user.id} className="border-b">
                  <td className="p-2">{user.name}</td>
                  <td className="p-2">{user.employee_id || '—'}</td>
                  <td className="p-2">{user.email}</td>
                  <td className="p-2">
                    {editUserId === user.id ? (
                      <select
                        className="border p-1"
                        value={editData.role}
                        onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                      >
                        <option value="">--Select--</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.name}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      user.role || '—'
                    )}
                  </td>
                  <td className="p-2">
                    {editUserId === user.id ? (
                      <select
                        className="border p-1"
                        value={editData.department_id}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            department_id: e.target.value,
                            section_id: '',
                            section_ids: [],
                          })
                        }
                      >
                        <option value="">--Dept--</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    ) : department ? (
                      department.name
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="p-2">
                    {editUserId === user.id ? (
                      editData.department_id && (
                        <select
                          className="border p-1"
                          value={editData.section_ids}
                          multiple
                          size={Math.min(5, Math.max(2, departments.find((dept) => dept.id === Number(editData.department_id))?.sections?.length || 2))}
                          onChange={(e) => {
                            const selectedSectionIds = Array.from(e.target.selectedOptions, (option) => option.value);
                            setEditData({
                              ...editData,
                              section_ids: selectedSectionIds,
                              section_id: selectedSectionIds[0] || '',
                            });
                          }}
                        >
                          <option value="">--Section--</option>
                          {departments
                            .find((dept) => dept.id === Number(editData.department_id))
                            ?.sections?.map((section) => (
                              <option key={section.id} value={section.id}>
                                {section.name}
                              </option>
                            ))}
                        </select>
                      )
                    ) : section ? (
                      section
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="p-2">
                    {editUserId === user.id ? (
                      <select
                        className="border p-1"
                        value={editData.warehouse_id}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            warehouse_id: e.target.value,
                          })
                        }
                      >
                        <option value="">--Warehouse--</option>
                        {warehouses.map((wh) => (
                          <option key={wh.id} value={wh.id}>
                            {wh.name}
                          </option>
                        ))}
                      </select>
                    ) : warehouseName ? (
                      warehouseName
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="p-2">
                    {editUserId === user.id ? (
                      <input
                        type="checkbox"
                        checked={editData.can_request_medication}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            can_request_medication: e.target.checked,
                          })
                        }
                      />
                    ) : user.can_request_medication ? (
                      uiText("Yes")
                    ) : (
                      uiText("No")
                    )}
                  </td>
                  <td className="p-2">{user.is_active ? uiText("Yes") : uiText("No")}</td>
                  <td className="p-2">
                    {editUserId === user.id ? (
                      <>
                        <button
                          onClick={() => saveEdit(user.id)}
                          className="text-green-600 mr-2"
                        >{uiText("Save")}</button>
                        <button onClick={cancelEdit} className="text-gray-600">{uiText("Cancel")}</button>
                      </>
                    ) : (
                      <>
                        {user.is_active && (
                          <button
                            onClick={() => deactivateUser(user.id)}
                            className="text-red-600 hover:underline mr-2"
                          >{uiText("Deactivate")}</button>
                        )}
                        <button
                          onClick={() => startEdit(user)}
                          className="text-blue-600 hover:underline mr-2"
                        >{uiText("Assign")}</button>
                        {canManagePermissions && (
                          <button
                            type="button"
                            onClick={() => openUserPermissionsEditor(user)}
                            className="text-indigo-600 hover:underline"
                          >
                            Permissions
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
    );
  };

  const renderAccountRequests = () => {
    if (!canManageAccountRequests) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage account requests.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-end">
        <div className="flex-1">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">{uiText("Search")}</label>
          <input
            type="search"
            className="mt-1 w-full rounded border border-gray-300 p-2"
            placeholder="Search by name or email"
            value={requestFilters.search}
            onChange={(e) =>
              setRequestFilters((prev) => ({ ...prev, search: e.target.value }))
            }
          />
        </div>
        <div className="md:w-48">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">{uiText("Status")}</label>
          <select
            className="mt-1 w-full rounded border border-gray-300 p-2"
            value={requestFilters.status}
            onChange={(e) =>
              setRequestFilters((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <option value="all">{uiText("All")}</option>
            <option value="pending">{uiText("Pending")}</option>
            <option value="approved">{uiText("Approved")}</option>
            <option value="rejected">{uiText("Rejected")}</option>
          </select>
        </div>
      </div>
      {requestsError && <p className="text-sm text-red-600 mb-2">{requestsError}</p>}
      {loadingRequests ? (
        <p>Loading account requests...</p>
      ) : filteredRequests.length === 0 ? (
        <p>No account requests found.</p>
      ) : (
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-200 text-left">
              <th className="p-2">{uiText("Name")}</th>
              <th className="p-2">{uiText("Email")}</th>
              <th className="p-2">{uiText("Department")}</th>
              <th className="p-2">{uiText("Section")}</th>
              <th className="p-2">{uiText("Status")}</th>
              <th className="p-2">{uiText("Submitted")}</th>
              <th className="p-2">Reviewer</th>
              <th className="p-2">{uiText("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((req) => {
              const createdAt = req.created_at ? new Date(req.created_at) : null;
              const reviewedAt = req.reviewed_at ? new Date(req.reviewed_at) : null;
              return (
                <tr key={req.id} className="border-b">
                  <td className="p-2">{req.name}</td>
                  <td className="p-2">{req.email}</td>
                  <td className="p-2">{req.department_name || req.department_id || '—'}</td>
                  <td className="p-2">{req.section_name || '—'}</td>
                  <td className="p-2 capitalize">{req.status}</td>
                  <td className="p-2">
                    {createdAt ? createdAt.toLocaleString() : '—'}
                    {req.status !== 'pending' && reviewedAt ? (
                      <div className="text-xs text-gray-500">
                        Reviewed: {reviewedAt.toLocaleString()}
                      </div>
                    ) : null}
                  </td>
                  <td className="p-2">{req.reviewer_name || '—'}</td>
                  <td className="p-2">
                    {req.status === 'pending' ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => approveAccountRequest(req.id)}
                          className="bg-green-600 text-white px-2 py-1 rounded"
                        >{uiText("Approve")}</button>
                        <button
                          onClick={() => rejectAccountRequest(req.id)}
                          className="bg-red-600 text-white px-2 py-1 rounded"
                        >{uiText("Reject")}</button>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-600">
                        {req.rejection_reason
                          ? `Reason: ${req.rejection_reason}`
                          : 'Processed'}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
    );
  };

  const renderDepartments = () => {
    if (!canManageDepartments) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage departments.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        {departmentsError && (
          <p className="mb-2 text-sm text-red-600">{departmentsError}</p>
        )}
        {organizationMessage && <p className="mb-2 text-sm text-green-700">{organizationMessage}</p>}
      {loadingDepartments ? (
        <p>{uiText("Loading departments...")}</p>
      ) : (
        <>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-200 text-left">
                <th className="p-2">{uiText("Department")}</th>
                <th className="p-2">{uiText("Type")}</th>
                <th className="p-2">Sections</th>
                <th className="p-2">{uiText("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDepartments.map((dept) => (
                <tr key={dept.id} className="border-b">
                  <td className="p-2 font-medium">
                    {editingDepartment?.id === dept.id ? (
                      <input className="w-full rounded border px-2 py-1" value={editingDepartment.name}
                        onChange={(e) => setEditingDepartment({ ...editingDepartment, name: e.target.value })} />
                    ) : dept.name}
                  </td>
                  <td className="p-2">
                    {editingDepartment?.id === dept.id ? (
                      <select className="rounded border px-2 py-1" value={editingDepartment.type?.toLowerCase()}
                        onChange={(e) => setEditingDepartment({ ...editingDepartment, type: e.target.value })}>
                        <option value="medical">{uiText("Medical")}</option>
                        <option value="operational">{uiText("Operational")}</option>
                      </select>
                    ) : <span className="rounded-full bg-blue-100 px-2 py-1 text-xs uppercase tracking-wide text-blue-700">{dept.type}</span>}
                  </td>
                  <td className="p-2">
                    {dept.sections?.length ? (
                      <ul className="ml-4 list-disc">
                        {dept.sections.map((section) => (
                          <li key={section.id} className="mb-1">
                            {editingSection?.id === section.id ? (
                              <span className="inline-flex gap-1">
                                <input className="rounded border px-2 py-1" value={editingSection.name}
                                  onChange={(e) => setEditingSection({ ...editingSection, name: e.target.value })} />
                                <button className="text-green-700" onClick={saveSection}>{uiText("Save")}</button>
                                <button className="text-gray-600" onClick={() => setEditingSection(null)}>{uiText("Cancel")}</button>
                              </span>
                            ) : <span>{section.name}{' '}<button className="text-xs text-blue-700 hover:underline"
                              onClick={() => setEditingSection({ id: section.id, departmentId: dept.id, name: section.name })}>{uiText("Edit")}</button></span>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="p-2">
                    {editingDepartment?.id === dept.id ? (
                      <span className="flex gap-2">
                        <button className="text-green-700 hover:underline" onClick={saveDepartment}>{uiText("Save")}</button>
                        <button className="text-gray-600 hover:underline" onClick={() => setEditingDepartment(null)}>{uiText("Cancel")}</button>
                      </span>
                    ) : <button className="text-blue-700 hover:underline"
                      onClick={() => setEditingDepartment({ id: dept.id, name: dept.name, type: dept.type })}>{uiText("Edit")}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="space-y-2 rounded border bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold uppercase text-gray-600">
                Add Department
              </h3>
              <input
                className="w-full rounded border px-3 py-2"
                placeholder="Department name"
                value={newDept.name}
                onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
              />
              <select
                className="w-full rounded border px-3 py-2"
                value={newDept.type}
                onChange={(e) => setNewDept({ ...newDept, type: e.target.value })}
              >
                <option value="medical">{uiText("Medical")}</option>
                <option value="operational">{uiText("Operational")}</option>
              </select>
              <button
                onClick={addDepartment}
                className="w-full rounded bg-blue-600 px-3 py-2 text-white"
              >
                Add department
              </button>
            </div>
            <div className="space-y-2 rounded border bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold uppercase text-gray-600">
                Add Section
              </h3>
              <select
                className="w-full rounded border px-3 py-2"
                value={newSection.department_id}
                onChange={(e) =>
                  setNewSection({ ...newSection, department_id: e.target.value })
                }
              >
                <option value="">{uiText("Select department")}</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
              <input
                className="w-full rounded border px-3 py-2"
                placeholder="Section name"
                value={newSection.name}
                onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
              />
              <button
                onClick={addSection}
                className="w-full rounded bg-blue-600 px-3 py-2 text-white"
              >
                Add section
              </button>
            </div>
          </div>
        </>
      )}
    </div>
    );
  };

  const renderWarehouses = () => {
    if (!canManageDepartments) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage warehouses.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {warehouseError && (
          <p className="text-sm text-red-600">{warehouseError}</p>
        )}
        {warehouseMessage && (
          <p className="text-sm text-green-700">{warehouseMessage}</p>
        )}
        {warehousesError && (
          <p className="text-sm text-red-600">{warehousesError}</p>
        )}
        <div className="overflow-x-auto">
          {warehousesLoading ? (
            <p>Loading warehouses...</p>
          ) : filteredWarehouses.length === 0 ? (
            <p>No warehouses found.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-200 text-left">
                  <th className="p-2">{uiText("Name")}</th>
                  <th className="p-2">{uiText("Type")}</th>
                  <th className="p-2">{uiText("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredWarehouses.map((warehouse) => (
                  <tr key={warehouse.id} className="border-b">
                    <td className="p-2 font-medium">
                      {editingWarehouseId === warehouse.id ? (
                        <input
                          className="w-full rounded border px-2 py-1"
                          value={editingWarehouseName}
                          onChange={(e) => setEditingWarehouseName(e.target.value)}
                          disabled={savingWarehouse}
                        />
                      ) : (
                        warehouse.name
                      )}
                    </td>
                    <td className="p-2">
                      <span className="rounded-full bg-amber-100 px-2 py-1 text-xs uppercase tracking-wide text-amber-700">
                        {warehouse.type || 'warehouse'}
                      </span>
                    </td>
                    <td className="p-2">
                      {editingWarehouseId === warehouse.id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={saveWarehouseEdit}
                            disabled={savingWarehouse}
                            className="rounded bg-green-600 px-3 py-1 text-white hover:bg-green-700 disabled:bg-gray-400"
                          >
                            {savingWarehouse ? uiText("Saving…") : uiText("Save")}
                          </button>
                          <button
                            onClick={cancelWarehouseEdit}
                            className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-100"
                          >{uiText("Cancel")}</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditingWarehouse(warehouse)}
                          className="rounded border border-blue-200 px-3 py-1 text-blue-700 hover:bg-blue-50"
                        >{uiText("Edit")}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-2 rounded border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold uppercase text-gray-600">Add Warehouse</h3>
          <input
            className="w-full rounded border px-3 py-2"
            placeholder="Warehouse name"
            value={warehouseForm.name}
            onChange={(e) => setWarehouseForm({ name: e.target.value })}
            disabled={savingWarehouse}
          />
          <button
            onClick={addWarehouse}
            disabled={savingWarehouse}
            className="w-full rounded bg-blue-600 px-3 py-2 text-white hover:bg-blue-700 disabled:bg-gray-400"
          >
            {savingWarehouse ? uiText("Saving…") : uiText("Add warehouse")}
          </button>
        </div>
      </div>
    );
  };

  const renderRoutes = () => {
    if (!canManageRoutes) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage approval routes.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        {routesError && <p className="mb-2 text-sm text-red-600">{routesError}</p>}
        {routesVersionLabel && (
          <p className="mb-2 text-xs text-gray-500">Active route version: {routesVersionLabel}</p>
        )}
        <div className="mb-4 rounded border border-blue-100 bg-blue-50 p-3">
          <p className="mb-2 text-sm font-medium text-blue-900">Simulation mode (safe preview)</p>
          <p className="mb-2 text-xs text-blue-800">
            Submit JSON with <code>changes</code> and <code>scenarios</code> to preview route impact without publishing.
          </p>
          <textarea
            className="mb-2 h-32 w-full rounded border p-2 font-mono text-xs"
            value={simulationInput}
            onChange={(e) => setSimulationInput(e.target.value)}
          />
          <button
            onClick={runRouteSimulation}
            className="rounded bg-blue-600 px-3 py-1 text-white hover:bg-blue-700"
          >
            Run simulation
          </button>
          {simulationResult && (
            <pre className="mt-2 overflow-auto rounded bg-white p-2 text-xs">
              {JSON.stringify(simulationResult, null, 2)}
            </pre>
          )}
        </div>
        {loadingRoutes ? (
          <p>Loading routes...</p>
        ) : (
          <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-200 text-left">
              <th className="p-2">{uiText("Type")}</th>
              <th className="p-2">Dept Type</th>
              <th className="p-2">{uiText("Level")}</th>
              <th className="p-2">{uiText("Role")}</th>
              <th className="p-2">Min</th>
              <th className="p-2">Max</th>
              <th className="p-2">{uiText("Warehouse")}</th>
              <th className="p-2">{uiText("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRoutes.map((route) => {
              const editing = editRoutes[route.id];
              const data = editing || route;
              return (
                <tr key={route.id} className="border-b">
                  <td className="p-2">
                    {editing ? (
                      <input
                        className="border p-1"
                        value={data.request_type}
                        onChange={(e) =>
                          setEditRoutes((prev) => ({
                            ...prev,
                            [route.id]: { ...data, request_type: e.target.value },
                          }))
                        }
                      />
                    ) : (
                      route.request_type
                    )}
                  </td>
                  <td className="p-2">
                    {editing ? (
                      <select
                        className="border p-1"
                        value={data.department_type}
                        onChange={(e) =>
                          setEditRoutes((prev) => ({
                            ...prev,
                            [route.id]: { ...data, department_type: e.target.value },
                          }))
                        }
                      >
                        <option value="medical">{uiText("Medical")}</option>
                        <option value="operational">{uiText("Operational")}</option>
                      </select>
                    ) : (
                      route.department_type
                    )}
                  </td>
                  <td className="p-2">
                    {editing ? (
                      <input
                        type="number"
                        className="border p-1 w-20"
                        value={data.approval_level ?? ''}
                        onChange={(e) =>
                          setEditRoutes((prev) => ({
                            ...prev,
                            [route.id]: { ...data, approval_level: e.target.value },
                          }))
                        }
                      />
                    ) : (
                      route.approval_level
                    )}
                  </td>
                  <td className="p-2">
                    {editing ? (
                      <select
                        className="border p-1"
                        value={data.role}
                        onChange={(e) =>
                          setEditRoutes((prev) => ({
                            ...prev,
                            [route.id]: { ...data, role: e.target.value },
                          }))
                        }
                      >
                        <option value="">--Role--</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.name}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      route.role
                    )}
                  </td>
                  <td className="p-2">
                    {editing ? (
                      <input
                        type="number"
                        className="border p-1 w-24"
                        value={data.min_amount ?? ''}
                        onChange={(e) =>
                          setEditRoutes((prev) => ({
                            ...prev,
                            [route.id]: { ...data, min_amount: e.target.value },
                          }))
                        }
                      />
                    ) : (
                      route.min_amount ?? '—'
                    )}
                  </td>
                  <td className="p-2">
                    {editing ? (
                      <input
                        type="number"
                        className="border p-1 w-24"
                        value={data.max_amount ?? ''}
                        onChange={(e) =>
                          setEditRoutes((prev) => ({
                            ...prev,
                            [route.id]: { ...data, max_amount: e.target.value },
                          }))
                        }
                      />
                    ) : (
                      route.max_amount ?? '—'
                    )}
                  </td>
                  <td className="p-2">
                    {editing ? (
                      <select
                        className="border p-1"
                        value={data.warehouse_id ?? ''}
                        onChange={(e) =>
                          setEditRoutes((prev) => ({
                            ...prev,
                            [route.id]: { ...data, warehouse_id: e.target.value },
                          }))
                        }
                      >
                        <option value="">Any</option>
                        {warehouses.map((warehouse) => (
                          <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                        ))}
                      </select>
                    ) : (
                      warehouses.find((w) => Number(w.id) === Number(route.warehouse_id))?.name || 'Any'
                    )}
                  </td>
                  <td className="p-2">
                    {editing ? (
                      <>
                        <button
                          onClick={() => saveRoute({ id: route.id, ...data })}
                          className="text-green-600 mr-2"
                        >{uiText("Save")}</button>
                        <button
                          onClick={() =>
                            setEditRoutes((prev) => {
                              const copy = { ...prev };
                              delete copy[route.id];
                              return copy;
                            })
                          }
                          className="text-gray-600"
                        >{uiText("Cancel")}</button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() =>
                            setEditRoutes((prev) => ({
                              ...prev,
                              [route.id]: {
                                request_type: route.request_type || '',
                                department_type: route.department_type || 'medical',
                                approval_level:
                                  route.approval_level === null
                                    ? ''
                                    : String(route.approval_level),
                                role: route.role || '',
                                min_amount:
                                  route.min_amount === null
                                    ? ''
                                    : String(route.min_amount),
                                max_amount:
                                  route.max_amount === null
                                    ? ''
                                    : String(route.max_amount),
                                warehouse_id:
                                  route.warehouse_id === null ? '' : String(route.warehouse_id),
                                id: route.id,
                              },
                            }))
                          }
                          className="text-blue-600 mr-2"
                        >{uiText("Edit")}</button>
                        <button
                          onClick={() => deleteRoute(route.id)}
                          className="text-red-600"
                        >{uiText("Delete")}</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td className="p-2">
                <input
                  className="border p-1"
                  value={newRoute.request_type}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, request_type: e.target.value }))
                  }
                />
              </td>
              <td className="p-2">
                <select
                  className="border p-1"
                  value={newRoute.department_type}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, department_type: e.target.value }))
                  }
                >
                  <option value="medical">{uiText("Medical")}</option>
                  <option value="operational">{uiText("Operational")}</option>
                </select>
              </td>
              <td className="p-2">
                <input
                  type="number"
                  className="border p-1 w-20"
                  value={newRoute.approval_level}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, approval_level: e.target.value }))
                  }
                />
              </td>
              <td className="p-2">
                <select
                  className="border p-1"
                  value={newRoute.role}
                  onChange={(e) => setNewRoute((prev) => ({ ...prev, role: e.target.value }))}
                >
                  <option value="">--Role--</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </td>
              <td className="p-2">
                <input
                  type="number"
                  className="border p-1 w-24"
                  value={newRoute.min_amount}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, min_amount: e.target.value }))
                  }
                />
              </td>
              <td className="p-2">
                <input
                  type="number"
                  className="border p-1 w-24"
                  value={newRoute.max_amount}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, max_amount: e.target.value }))
                  }
                />
              </td>
              <td className="p-2">
                <select
                  className="border p-1"
                  value={newRoute.warehouse_id}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, warehouse_id: e.target.value }))
                  }
                >
                  <option value="">Any</option>
                  {warehouses.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                  ))}
                </select>
              </td>
              <td className="p-2">
                <select
                  className="border p-1"
                  value={newRoute.warehouse_id}
                  onChange={(e) =>
                    setNewRoute((prev) => ({ ...prev, warehouse_id: e.target.value }))
                  }
                >
                  <option value="">Any</option>
                  {warehouses.map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
                  ))}
                </select>
              </td>
              <td className="p-2">
                <button
                  onClick={() => saveRoute(newRoute)}
                  className="bg-green-600 text-white px-2 py-1 rounded"
                >{uiText("Add")}</button>
              </td>
            </tr>
          </tbody>
        </table>
        )}
    </div>
    );
  };

  const renderAutoAssignments = () => {
    if (!canManageAutoAssignments) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage request auto-assignment rules.
        </div>
      );
    }

    const renderRuleInputs = (rule, setRule, saveLabel, onSave) => (
      <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-5 md:items-end">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">{uiText("Request type")}</label>
          <select
            className="mt-1 w-full rounded border border-gray-300 p-2"
            value={rule.request_type}
            onChange={(e) => {
              const nextType = e.target.value;
              setRule((prev) => ({
                ...prev,
                request_type: nextType,
                warehouse_id: nextType === 'Stock' ? prev.warehouse_id : '',
              }));
            }}
          >
            {autoAssignmentTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Submitted warehouse</label>
          <select
            className="mt-1 w-full rounded border border-gray-300 p-2 disabled:bg-gray-100"
            value={rule.warehouse_id || ''}
            disabled={rule.request_type !== 'Stock'}
            onChange={(e) => setRule((prev) => ({ ...prev, warehouse_id: e.target.value }))}
          >
            <option value="">{rule.request_type === 'Stock' ? '--Select warehouse--' : 'Not applicable'}</option>
            {autoAssignmentWarehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Assign to</label>
          <select
            className="mt-1 w-full rounded border border-gray-300 p-2"
            value={rule.assignee_user_id || ''}
            onChange={(e) => setRule((prev) => ({ ...prev, assignee_user_id: e.target.value }))}
          >
            <option value="">--Select user--</option>
            {autoAssignmentAssignees.map((assignee) => (
              <option key={assignee.id} value={assignee.id}>
                {assignee.name} ({assignee.role})
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={rule.is_active !== false}
            onChange={(e) => setRule((prev) => ({ ...prev, is_active: e.target.checked }))}
          />{uiText("Active")}</label>
        <button
          type="button"
          onClick={onSave}
          className="rounded bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700"
        >
          {saveLabel}
        </button>
      </div>
    );

    return (
      <div className="space-y-4">
        <div className="rounded border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
          <h3 className="text-base font-semibold">Automatic assignment after final approval</h3>
          <p>
            Admins and SCM can route approved Maintenance, Non-stock, Stock, IT, Logbook,
            Medical Device, and Medication requests directly to a selected procurement user.
            Stock rules require the warehouse submitted with the stock request.
          </p>
        </div>

        {autoAssignmentError && <p className="text-sm text-red-600">{autoAssignmentError}</p>}
        {autoAssignmentMessage && <p className="text-sm text-green-700">{autoAssignmentMessage}</p>}

        {renderRuleInputs(
          autoAssignmentForm,
          setAutoAssignmentForm,
          'Save rule',
          () => saveAutoAssignmentRule(autoAssignmentForm),
        )}

        {loadingAutoAssignments ? (
          <p>Loading auto-assignment rules...</p>
        ) : autoAssignmentRules.length === 0 ? (
          <p className="text-sm text-gray-500">No auto-assignment rules configured yet.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-200 text-left">
                <th className="p-2">{uiText("Request type")}</th>
                <th className="p-2">Submitted warehouse</th>
                <th className="p-2">{uiText("Assigned user")}</th>
                <th className="p-2">{uiText("Status")}</th>
                <th className="p-2">{uiText("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredAutoAssignmentRules.map((rule) => {
                const isEditing = editingAutoAssignmentId === rule.id;
                return (
                  <tr key={rule.id} className="border-b align-top">
                    {isEditing ? (
                      <td colSpan="5" className="p-2">
                        {renderRuleInputs(
                          editingAutoAssignment,
                          setEditingAutoAssignment,
                          'Update rule',
                          () => saveAutoAssignmentRule(editingAutoAssignment, rule.id),
                        )}
                        <button
                          type="button"
                          onClick={() => setEditingAutoAssignmentId(null)}
                          className="mt-2 text-sm text-gray-600 hover:underline"
                        >
                          Cancel editing
                        </button>
                      </td>
                    ) : (
                      <>
                        <td className="p-2">{rule.request_type}</td>
                        <td className="p-2">{rule.warehouse_name || 'Not applicable'}</td>
                        <td className="p-2">{rule.assignee_name || rule.assignee_user_id}</td>
                        <td className="p-2">{rule.is_active ? uiText("Active") : 'Inactive'}</td>
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingAutoAssignmentId(rule.id);
                              setEditingAutoAssignment({
                                request_type: rule.request_type,
                                warehouse_id: rule.warehouse_id ? String(rule.warehouse_id) : '',
                                assignee_user_id: rule.assignee_user_id ? String(rule.assignee_user_id) : '',
                                is_active: rule.is_active !== false,
                              });
                            }}
                            className="mr-2 text-blue-600 hover:underline"
                          >{uiText("Edit")}</button>
                          <button
                            type="button"
                            onClick={() => deleteAutoAssignmentRule(rule.id)}
                            className="text-red-600 hover:underline"
                          >{uiText("Delete")}</button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };


  const renderPrintServices = () => {
    if (!canManagePrintServiceSettings) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage print service settings.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <DocumentBrandingSettings />
        <div className="rounded border border-cyan-100 bg-cyan-50 p-4 text-sm text-cyan-900">
          <h3 className="text-base font-semibold">IT Department Queue access</h3>
          <p>
            Link the IT Department Queue to the department whose users should view the queue and
            accept, complete, or claim print service requests.
          </p>
        </div>
        {printSettingsError && <p className="text-sm text-red-600">{printSettingsError}</p>}
        {printSettingsMessage && <p className="text-sm text-green-700">{printSettingsMessage}</p>}
        {loadingPrintSettings ? (
          <p>Loading print service settings...</p>
        ) : (
          <div className="space-y-3 rounded border bg-white p-4 shadow-sm">
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Linked IT department
              </span>
              <select
                className="mt-1 w-full rounded border border-gray-300 p-2"
                value={printSettingsDepartmentId}
                onChange={(e) => setPrintSettingsDepartmentId(e.target.value)}
                disabled={savingPrintSettings}
              >
                <option value="">-- Select department --</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>{department.name}</option>
                ))}
              </select>
            </label>
            <p className="text-xs text-gray-600">
              Current link: {printSettings?.department_name || 'Not configured; legacy IT-name matching is used.'}
            </p>
            {printSettings?.updated_at && (
              <p className="text-xs text-gray-500">{uiText("Last updated")}{" "}{new Date(printSettings.updated_at).toLocaleString()}
                {printSettings.updated_by_name ? ` by ${printSettings.updated_by_name}` : ''}.
              </p>
            )}
            <button
              type="button"
              onClick={savePrintServiceSettings}
              disabled={savingPrintSettings || !printSettingsDepartmentId}
              className={`rounded px-4 py-2 font-semibold text-white ${
                savingPrintSettings || !printSettingsDepartmentId
                  ? 'bg-gray-400'
                  : 'bg-cyan-600 hover:bg-cyan-700'
              }`}
            >
              {savingPrintSettings ? uiText("Saving…") : 'Save queue department'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderRoles = () => {
    if (!canManageRoles) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage roles.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 text-white shadow-md">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
                Role directory
              </p>
              <h3 className="text-xl font-semibold">Manage who can access what</h3>
              <p className="text-sm opacity-90">
                Roles can be managed by users with <span className="font-semibold">roles.manage</span> or
                <span className="font-semibold"> permissions.manage</span>.
              </p>
            </div>
            <div className="grid w-full max-w-md grid-cols-2 gap-3 text-sm md:w-auto md:grid-cols-3">
              <div className="rounded-lg bg-white/15 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide opacity-80">Roles</p>
                <p className="text-lg font-bold">{roles.length || 0}</p>
              </div>
              <div className="rounded-lg bg-white/15 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide opacity-80">{uiText("Status")}</p>
                <p className="flex items-center gap-1 text-lg font-semibold">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-300" aria-hidden />{uiText("Active")}</p>
              </div>
              <div className="col-span-2 rounded-lg bg-white/15 px-3 py-2 md:col-span-1">
                <p className="text-[11px] uppercase tracking-wide opacity-80">Access</p>
                <p className="text-sm font-semibold">roles.manage · permissions.manage</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded border bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Add Role</p>
              <p className="text-sm text-gray-600">Create reusable permission groups for your teams.</p>
            </div>
            {roles.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                <span className="h-2 w-2 rounded-full bg-blue-500" aria-hidden />
                {roles.length} configured
              </span>
            )}
          </div>
          <form onSubmit={handleCreateRole} className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="flex-1">
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Role name
              </span>
              <input
                className="mt-1 w-full rounded border px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Enter role name"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
              />
            </label>
            <button
              type="submit"
              className="rounded bg-blue-600 px-4 py-2 text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
              disabled={savingRole}
            >
              {savingRole ? uiText("Saving…") : 'Add role'}
            </button>
          </form>
          {roleSuccess && (
            <p className="mt-3 rounded border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {roleSuccess}
            </p>
          )}
          {rolesError && (
            <p className="mt-3 rounded border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
              {rolesError}
            </p>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {rolesLoading ? (
            <p className="p-4">Loading roles...</p>
          ) : filteredRoles.length === 0 ? (
            <div className="flex items-start gap-3 p-4 text-sm text-gray-700">
              <div className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-full bg-blue-50 text-center text-base font-semibold leading-8 text-blue-700">
                ℹ️
              </div>
              <div>
                <p className="font-semibold text-gray-900">No roles defined yet</p>
                <p className="text-gray-600">Create a role to start grouping permissions for your teams.</p>
              </div>
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                  <th className="px-3 py-2">{uiText("Name")}</th>
                  <th className="px-3 py-2 text-right">{uiText("Actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRoles.map((role) => (
                  <tr key={role.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 align-middle">
                      {editingRole.id === role.id ? (
                        <input
                          className="w-full rounded border px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          value={editingRole.name}
                          onChange={(e) =>
                            setEditingRole((prev) => ({ ...prev, name: e.target.value }))
                          }
                        />
                      ) : (
                        <span className="font-medium text-gray-900">{role.name}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editingRole.id === role.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={handleUpdateRole}
                            className="rounded bg-emerald-600 px-3 py-1 text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
                            disabled={savingRole}
                          >{uiText("Save")}</button>
                          <button
                            onClick={cancelEditingRole}
                            className="rounded border px-3 py-1 text-gray-700 transition hover:bg-gray-50"
                            type="button"
                          >{uiText("Cancel")}</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => startEditingRole(role)}
                            className="rounded border px-3 py-1 text-gray-800 transition hover:bg-gray-50"
                            type="button"
                          >{uiText("Edit")}</button>
                          <button
                            onClick={() => handleDeleteRole(role.id)}
                            className="rounded bg-red-600 px-3 py-1 text-white shadow-sm transition hover:bg-red-700 disabled:opacity-60"
                            disabled={deletingRoleId === role.id}
                          >
                            {deletingRoleId === role.id ? 'Deleting…' : uiText("Delete")}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderProjects = () => {
    if (!canManageProjects) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage projects.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="rounded border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold uppercase text-gray-600 mb-3">
            Add Project
          </h3>
        <form onSubmit={addProject} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex-1">
            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Project name
            </span>
            <input
              className="mt-1 w-full rounded border px-3 py-2"
              placeholder="Enter new project name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
            />
          </label>
          <label className="flex-1">
            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Visible departments
            </span>
            <DepartmentVisibilityChecklist
              departments={departments}
              selectedDepartmentIds={newProjectDepartmentIds}
              onChange={setNewProjectDepartmentIds}
              disabled={loadingDepartments}
              ariaLabel="Departments that can see the new project"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Keep All departments checked to make the project visible to everyone, or check specific departments.
            </span>
          </label>
          <button
            type="submit"
            className="rounded bg-blue-600 px-4 py-2 text-white"
            disabled={loadingProjects}
          >
            Add project
          </button>
        </form>
        {projectMessage && (
          <p className="mt-2 text-sm text-emerald-600">{projectMessage}</p>
        )}
        {projectError && <p className="mt-2 text-sm text-red-600">{projectError}</p>}
      </div>
        <div className="overflow-x-auto rounded border bg-white shadow-sm">
          {loadingProjects ? (
            <p className="p-4">Loading projects...</p>
          ) : filteredProjects.length === 0 ? (
            <p className="p-4 text-sm text-gray-600">No projects available.</p>
          ) : (
            <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-200 text-left">
                <th className="p-2">{uiText("Name")}</th>
                <th className="p-2">{uiText("Active")}</th>
                <th className="p-2">Visible to</th>
                <th className="p-2">Created</th>
                <th className="p-2">{uiText("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => {
                const createdAt = project.created_at ? new Date(project.created_at) : null;
                return (
                  <tr key={project.id} className="border-b">
                    <td className="p-2 font-medium text-gray-900">{project.name}</td>
                    <td className="p-2">{project.is_active !== false ? uiText("Yes") : uiText("No")}</td>
                    <td className="p-2 min-w-[18rem]">
                      <DepartmentVisibilityChecklist
                        departments={departments}
                        selectedDepartmentIds={getProjectDepartmentIds(project)}
                        onChange={(departmentIds) => updateProjectVisibility(project, departmentIds)}
                        disabled={loadingDepartments || savingProjectVisibilityId === project.id}
                        ariaLabel={`Departments that can see ${project.name}`}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        {savingProjectVisibilityId === project.id
                          ? 'Saving visibility...'
                          : formatProjectVisibility(project)}
                      </p>
                    </td>
                    <td className="p-2">
                      {createdAt ? createdAt.toLocaleDateString() : '—'}
                    </td>
                    <td className="p-2">
                      {project.is_active !== false ? (
                        <button
                          onClick={() => deactivateProject(project)}
                          className="text-red-600 hover:underline"
                        >{uiText("Deactivate")}</button>
                      ) : (
                        <span className="text-xs text-gray-500">Inactive</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          )}
      </div>
    </div>
    );
  };

  const renderPermissions = () => {
    if (!canManagePermissions) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage user permissions.
        </div>
      );
    }

    const filteredPermissions = availablePermissions.filter((permission) =>
      matchesManagementSearch(permission.name, permission.description, permission.code));

    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end">
          <div className="space-y-3 md:w-80">
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Search users
              </span>
              <span className="relative mt-1 block">
                <Search
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  className="w-full rounded border border-gray-300 py-2 pl-9 pr-3"
                  value={permissionUserSearch}
                  onChange={(event) => setPermissionUserSearch(event.target.value)}
                  placeholder="Search by name or email"
                />
              </span>
            </label>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">{uiText("User")}</label>
            <select
              className="mt-1 w-full rounded border border-gray-300 p-2"
              value={selectedPermissionUserId}
              onChange={(e) => {
                setSelectedPermissionUserId(e.target.value);
                setPermissionUserSearch('');
              }}
            >
              <option value="">Select user</option>
              {permissionUserOptions.map((permissionUser) => (
                <option key={permissionUser.id} value={permissionUser.id}>
                  {permissionUser.name}
                  {permissionUser.email ? ` (${permissionUser.email})` : ''}
                </option>
              ))}
            </select>
            {permissionUserSearch.trim() && matchingPermissionUsers.length === 0 && (
              <p className="text-xs text-gray-500">No users match your search.</p>
            )}
          </div>
          <div className="flex-1 text-sm text-gray-600">
            {permissionsSuccess && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-emerald-700">
                {permissionsSuccess}
              </div>
            )}
            {permissionsError && (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-red-700">
                {permissionsError}
              </div>
            )}
          </div>
        </div>
        <div className="rounded border border-gray-200 bg-white p-4 shadow-sm">
          {permissionsLoading ? (
            <p>Loading permissions...</p>
          ) : !selectedPermissionUserId ? (
            <p className="text-sm text-gray-600">
              Select a user to view and update their permissions.
            </p>
          ) : availablePermissions.length === 0 ? (
            <p className="text-sm text-gray-600">No permissions available.</p>
          ) : filteredPermissions.length === 0 ? (
            <p className="text-sm text-gray-600">
              No permissions match “{managementSearch.trim()}”.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {filteredPermissions.map((permission) => {
                const checked = selectedUserPermissions.includes(permission.code);
                return (
                  <label
                    key={permission.code}
                    className="flex cursor-pointer items-start gap-3 rounded border border-gray-200 bg-gray-50 p-3 hover:border-blue-300"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={checked}
                      onChange={() => togglePermissionForUser(permission.code)}
                    />
                    <div>
                      <div className="text-sm font-semibold text-gray-800">
                        {permission.name}
                      </div>
                      <p className="text-xs text-gray-600">{permission.description}</p>
                      <p className="text-xs text-gray-400">Code: {permission.code}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <button
            onClick={saveUserPermissions}
            disabled={savingPermissions || !selectedPermissionUserId}
            className={`rounded px-4 py-2 text-white ${
              savingPermissions || !selectedPermissionUserId
                ? 'bg-gray-400'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {savingPermissions ? uiText("Saving...") : 'Save permissions'}
          </button>
        </div>
      </div>
    );
  };

  const renderInterfaceAccess = () => {
    if (!canManagePermissions) {
      return (
        <div className="rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
          You do not have permission to manage interface access.
        </div>
      );
    }

    const sortedResources = [...uiResources]
      .filter((resource) => matchesManagementSearch(
        resource.label,
        resource.description,
        resource.key,
        ...(resource.permissions || []),
      ))
      .sort((a, b) => {
        const labelA = a.label?.toLowerCase?.() ?? '';
        const labelB = b.label?.toLowerCase?.() ?? '';
        return labelA.localeCompare(labelB);
      });

    const interfaceError = accessConfigError || uiAccessLoadError;

    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Configure which permission codes unlock each UI feature. Leave the list empty to make a
          feature available to all authenticated users.
        </p>
        {interfaceError && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {interfaceError}
          </div>
        )}
        {accessConfigSuccess && (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            {accessConfigSuccess}
          </div>
        )}
        <div className="overflow-x-auto rounded border border-gray-200 bg-white p-4 shadow-sm">
          {uiAccessLoading ? (
            <p>Loading interface access configuration...</p>
          ) : uiResources.length === 0 ? (
            <p className="text-sm text-gray-600">No interface resources have been registered yet.</p>
          ) : sortedResources.length === 0 ? (
            <p className="text-sm text-gray-600">
              No interface access entries match “{managementSearch.trim()}”.
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="p-2">Feature</th>
                  <th className="p-2">{uiText("Description")}</th>
                  <th className="p-2">Permissions</th>
                  <th className="p-2">Requirement</th>
                  <th className="p-2">{uiText("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedResources.map((resource) => {
                  const isEditing = editingAccessKey === resource.key;
                  const activePermissions = isEditing
                    ? editingAccessPermissions
                    : resource.permissions;
                  const showSelectedList = activePermissions && activePermissions.length > 0;

                  return (
                    <tr key={resource.key} className="border-b align-top">
                      <td className="p-2 font-semibold text-gray-800">{resource.label}</td>
                      <td className="p-2 text-gray-600">{resource.description || '—'}</td>
                      <td className="p-2">
                        {isEditing ? (
                          <div className="space-y-3">
                            {permissionsLoading ? (
                              <p className="text-sm text-gray-600">Loading permission catalog...</p>
                            ) : availablePermissions.length === 0 ? (
                              <p className="text-sm text-gray-600">
                                No permission catalog found. Add custom codes below.
                              </p>
                            ) : (
                              <div className="grid gap-2 md:grid-cols-2">
                                {availablePermissions.map((permission) => {
                                  const code = permission.code?.toLowerCase?.() ?? '';
                                  const checked = editingAccessPermissions.some(
                                    (entry) => entry.toLowerCase() === code
                                  );
                                  return (
                                    <label
                                      key={permission.code}
                                      className="flex cursor-pointer items-start gap-3 rounded border border-gray-200 bg-gray-50 p-3 hover:border-blue-300"
                                    >
                                      <input
                                        type="checkbox"
                                        className="mt-1 h-4 w-4"
                                        checked={checked}
                                        onChange={() => toggleAccessPermissionForResource(permission.code)}
                                      />
                                      <div>
                                        <div className="text-sm font-semibold text-gray-800">
                                          {permission.name}
                                        </div>
                                        <p className="text-xs text-gray-600">{permission.description}</p>
                                        <p className="text-xs text-gray-400">Code: {permission.code}</p>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                              <input
                                type="text"
                                value={customAccessPermission}
                                onChange={(e) => setCustomAccessPermission(e.target.value)}
                                placeholder="Add permission code"
                                className="w-full rounded border border-gray-300 p-2 text-sm sm:w-auto"
                              />
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={addCustomPermissionToResource}
                                  disabled={!customAccessPermission.trim()}
                                  className={`rounded px-3 py-1 text-sm font-semibold text-white ${
                                    customAccessPermission.trim()
                                      ? 'bg-blue-600 hover:bg-blue-700'
                                      : 'bg-gray-400'
                                  }`}
                                >
                                  Add code
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingAccessPermissions([])}
                                  className="rounded border border-gray-300 px-3 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                                >{uiText("Clear all")}</button>
                              </div>
                            </div>
                            {editingAccessPermissions.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {editingAccessPermissions.map((code) => (
                                  <span
                                    key={code}
                                    className="rounded bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700"
                                  >
                                    {code}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500">No permission required.</p>
                            )}
                          </div>
                        ) : showSelectedList ? (
                          <div className="flex flex-wrap gap-2">
                            {activePermissions.map((code) => (
                              <span
                                key={code}
                                className="rounded bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700"
                              >
                                {code}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">No permission required</span>
                        )}
                      </td>
                      <td className="p-2">
                        {isEditing ? (
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={editingAccessRequireAll}
                              onChange={(e) => setEditingAccessRequireAll(e.target.checked)}
                              className="h-4 w-4"
                            />
                            Require all selected permissions
                          </label>
                        ) : showSelectedList ? (
                          <span className="text-xs text-gray-600">
                            {resource.requireAll
                              ? 'All selected permissions'
                              : 'Any selected permission'}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">Not required</span>
                        )}
                      </td>
                      <td className="p-2">
                        {isEditing ? (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={saveInterfaceAccess}
                              disabled={savingAccessConfig}
                              className={`rounded px-3 py-1 text-sm font-semibold text-white ${
                                savingAccessConfig
                                  ? 'bg-gray-400'
                                  : 'bg-green-600 hover:bg-green-700'
                              }`}
                            >
                              {savingAccessConfig ? uiText("Saving…") : uiText("Save")}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditingAccessResource}
                              className="rounded border border-gray-300 px-3 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                            >{uiText("Cancel")}</button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEditingAccessResource(resource)}
                            className="rounded border border-blue-200 px-3 py-1 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                          >{uiText("Edit")}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderContractApprovalRules = () => {
    let parsedRules = null;
    let parsedRulesError = '';

    try {
      parsedRules = JSON.parse(contractRulesDraft);
    } catch {
      parsedRulesError = 'Preview unavailable due to invalid JSON.';
    }

    return (
      <div className="space-y-4">
        <div className="overflow-x-auto">
          <h3 className="mb-2 text-lg font-semibold text-slate-900">Contract Approval Rules</h3>
          <p className="mb-3 text-sm text-slate-600">Configure contract approvals in the same table-style flow used for request approval rules.</p>

          {parsedRulesError ? (
            <p className="mb-3 text-sm text-rose-600">{parsedRulesError}</p>
          ) : (
            <>
              <table className="mb-3 min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-200 text-left">
                    <th className="p-2">{uiText("Level")}</th>
                    <th className="p-2">Stage Key</th>
                    <th className="p-2">Stage Label</th>
                    <th className="p-2">{uiText("Role")}</th>
                    <th className="p-2">Suggest Edits</th>
                    <th className="p-2">{uiText("Description")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(parsedRules?.stages || []).map((stage, idx) => (
                    <tr key={stage.key || idx} className="border-b">
                      <td className="p-2">{idx + 1}</td>
                      <td className="p-2">{stage.key || '—'}</td>
                      <td className="p-2">{stage.label || '—'}</td>
                      <td className="p-2">{stage.roleType || '—'}</td>
                      <td className="p-2">{stage.canSuggestEdits ? uiText("Yes") : uiText("No")}</td>
                      <td className="p-2">{stage.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
                <p className="font-medium text-slate-900">Edit gate roles</p>
                <p className="mt-1 text-slate-700">{Array.isArray(parsedRules?.editGateRoles) && parsedRules.editGateRoles.length > 0 ? parsedRules.editGateRoles.join(', ') : '—'}</p>
                <p className="mt-2 text-slate-600">Max draft edit rounds: {parsedRules?.maxDraftEditRounds ?? '—'}</p>
              </div>
            </>
          )}

          <textarea
            value={contractRulesDraft}
            onChange={(e) => setContractRulesDraft(e.target.value)}
            rows={16}
            className="mb-3 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs"
          />

          {contractRulesError && <p className="mb-2 text-sm text-rose-600">{contractRulesError}</p>}
          {contractRulesSuccess && <p className="mb-2 text-sm text-emerald-700">{contractRulesSuccess}</p>}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                try {
                  const parsed = JSON.parse(contractRulesDraft);
                  if (!Array.isArray(parsed?.stages) || parsed.stages.length === 0) {
                    setContractRulesError('Rules must include at least one stage.');
                    setContractRulesSuccess('');
                    return;
                  }
                  if (!Array.isArray(parsed?.editGateRoles) || parsed.editGateRoles.length === 0) {
                    setContractRulesError('Rules must include at least one edit gate role.');
                    setContractRulesSuccess('');
                    return;
                  }
                  localStorage.setItem('contract-approval-rules', JSON.stringify(parsed));
                  setContractRulesError('');
                  setContractRulesSuccess('Contract approval rules saved.');
                } catch {
                  setContractRulesError('Invalid JSON. Please fix and try again.');
                  setContractRulesSuccess('');
                }
              }}
              className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
            >
              Save rules
            </button>
            <button
              type="button"
              onClick={() => {
                try {
                  const saved = localStorage.getItem('contract-approval-rules');
                  const parsed = saved ? JSON.parse(saved) : defaultContractApprovalRules;
                  setContractRulesDraft(JSON.stringify(parsed, null, 2));
                  setContractRulesError('');
                  setContractRulesSuccess('Loaded current rules.');
                } catch {
                  setContractRulesError('Failed to load current rules.');
                  setContractRulesSuccess('');
                }
              }}
              className="rounded border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:bg-slate-100"
            >
              Load current rules
            </button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <>
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 shadow-lg">
            <div className="absolute inset-0 opacity-10" aria-hidden>
              <div className="h-full w-full bg-[radial-gradient(circle_at_20%_20%,white,transparent_35%),radial-gradient(circle_at_80%_0%,white,transparent_25%),radial-gradient(circle_at_40%_80%,white,transparent_30%)]" />
            </div>
            <div className="relative flex flex-col gap-2 text-white sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-blue-100">Control Center</p>
                <h2 className="text-3xl font-bold">System Management</h2>
                <p className="mt-2 max-w-3xl text-sm text-blue-100">
                  Monitor usage, manage approvals, and keep your operational teams aligned from a single dashboard.
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 backdrop-blur">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-lg font-bold">
                  ⚙️
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-blue-100">Quick insight</p>
                  <p className="text-sm font-semibold">{availableTabs.length} active modules</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="group relative overflow-hidden rounded-xl border border-blue-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Active users</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{activeUserCount}</p>
                  <p className="text-xs text-slate-500">{uiText("of")}{" "}{users.length} total</p>
                </div>
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">Live</span>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-xl border border-emerald-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400" />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Departments</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{departments.length}</p>
                  <p className="text-xs text-slate-500">Across all types</p>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Org</span>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-xl border border-amber-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400" />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Warehouses</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{warehouses.length}</p>
                  <p className="text-xs text-slate-500">Available locations</p>
                </div>
                <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">{uiText("Stock")}</span>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-xl border border-indigo-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-400 via-blue-400 to-sky-400" />
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Active projects</p>
                  <p className="mt-2 text-3xl font-bold text-slate-900">{activeProjectCount}</p>
                  <p className="text-xs text-slate-500">Management overview</p>
                </div>
                <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">Ops</span>
              </div>
            </div>
          </div>

          <div className="mt-8 grid items-start gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
            <aside className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm lg:sticky lg:top-6" aria-label="Management sections">
              <div className="px-3 pb-3 pt-2">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{uiText("Administration")}</p>
                <p className="mt-1 text-sm text-slate-600">Choose a workspace to manage.</p>
              </div>
              <nav className="space-y-4">
                {navigationGroups.map(({ group, items }) => (
                  <div key={group}>
                    <p className="mb-1 px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">{group}</p>
                    <div className="space-y-1">
                      {items.map((id) => {
                        const [label, , Icon] = managementTabs[id];
                        const selected = tab === id;
                        return (
                          <button key={id} type="button" onClick={() => setTab(id)} aria-current={selected ? 'page' : undefined}
                            className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition ${selected ? 'bg-blue-600 text-white shadow-sm shadow-blue-200' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'}`}>
                            <Icon className={`h-4 w-4 shrink-0 ${selected ? 'text-blue-100' : 'text-slate-400'}`} aria-hidden="true" />
                            <span className="flex-1">{label}</span>
                            <ChevronRight className={`h-4 w-4 ${selected ? 'opacity-80' : 'opacity-0 group-hover:opacity-40'}`} aria-hidden="true" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>
            <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" aria-labelledby="management-section-title">
              <header className="flex flex-col gap-4 border-b border-slate-200 px-5 py-5 sm:flex-row sm:items-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                  {React.createElement(currentTab[2], { className: 'h-5 w-5', 'aria-hidden': true })}
                </span>
                <div><h3 id="management-section-title" className="text-lg font-bold text-slate-900">{currentTab[0]}</h3><p className="text-sm text-slate-500">{currentTab[1]}</p></div>
                {['departments', 'warehouses', 'projects', 'roles', 'routes', 'autoAssignments', 'permissions', 'interfaceAccess'].includes(tab) && (
                  <label className="relative sm:ml-auto sm:w-72">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                    <span className="sr-only">{uiText("Search")}{" "}{currentTab[0].toLowerCase()}</span>
                    <input type="search" value={managementSearch} onChange={(e) => setManagementSearch(e.target.value)}
                      placeholder={`Search ${currentTab[0].toLowerCase()}`}
                      className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100" />
                  </label>
                )}
              </header>
            <div className="p-4">
              {tab === 'users' && renderUsers()}
              {tab === 'accountRequests' && renderAccountRequests()}
              {tab === 'departments' && renderDepartments()}
              {tab === 'warehouses' && renderWarehouses()}
              {tab === 'routes' && renderRoutes()}
              {tab === 'autoAssignments' && renderAutoAssignments()}
              {tab === 'printServices' && renderPrintServices()}
              {tab === 'projects' && renderProjects()}
              {tab === 'roles' && renderRoles()}
              {tab === 'itemIdentity' && <ProcurementIdentitySettings />}
              {tab === 'approvalReporting' && <MaintenanceApprovalReportingSettings />}
              {tab === 'technicalReview' && <TechnicalReviewSettings />}
              {tab === 'surgicalConsignment' && <SurgicalConsignmentSettings />}
              {tab === 'permissions' && renderPermissions()}
              {tab === 'interfaceAccess' && renderInterfaceAccess()}
              {tab === 'contractApprovalRules' && renderContractApprovalRules()}
            </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Management;
